<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:toggle-right',
	name: '$t:blocks.input_switch.name',
	description: '$t:blocks.input_switch.description',
	category: 'input',
	fieldBinding: { supportedTypes: ['boolean'] },

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'label',
			label: '$t:blocks.common.props.label',
		},
		{
			name: 'description',
			label: '$t:blocks.common.props.description',
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'checkedIcon',
			label: '$t:blocks.input_switch.props.checked_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'uncheckedIcon',
			label: '$t:blocks.input_switch.props.unchecked_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'loading',
			label: '$t:blocks.common.props.loading',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	],
}
</script>
<script lang="ts" setup>
import type { SwitchProps } from '@nuxt/ui';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice, useReadonly } from '#layers/ui/public';

interface InputSwitchBlockProps extends
	InputBlockProps,
	Pick<SwitchProps, 'color' | 'size' | 'description' | 'label' | 'loading' | 'loadingIcon' | 'checkedIcon' | 'uncheckedIcon' | 'required'> {
}

const props = defineProps<InputSwitchBlockProps>();
// tri-state: unbound stays `undefined` (field unset), not coerced to `false`
const model = defineModel<boolean | undefined>({ default: undefined });

;

const forwarded = reactivePick(props, 'color', 'size', 'description', 'label', 'loading', 'loadingIcon', 'checkedIcon', 'uncheckedIcon', 'required');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly ?? false);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice" :content="{ align: 'center' }">
		<div class="w-min!">
			<DSwitch
				v-bind="forwarded"
				v-model="modelValue"
				:disabled
				:aria-readonly="readonly"
				:class="{ 'pointer-events-none': readonly }"
				@pointerdown.capture="preventPointer"
				@click.capture="preventPointer"
				@keydown.capture="preventKeyboardNavigation"
			/>
		</div>
	</DTooltip>
</template>
